class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    const bulkItems = Array.isArray(parsedState.items) ? parsedState.items : null;
    this.cartItemKey = parsedState.key || bulkItems?.[0]?.key;
    const productTarget = document.getElementById('cart-notification-product');
    this.notification.classList.toggle('ld-bulk-notification', Boolean(bulkItems));
    productTarget.style.display = '';
    productTarget.style.maxHeight = '';
    productTarget.style.overflowY = '';
    if (bulkItems) {
      const html = new DOMParser().parseFromString(parsedState.sections['cart-notification-product'], 'text/html');
      const fragment = document.createDocumentFragment();
      const groups = new Map();
      bulkItems.forEach(item => {
        const prefix = 'cart-notification-product-';
        const source = html.getElementById(prefix + item.key) || Array.from(html.querySelectorAll('[id^="cart-notification-product-"]')).find(row => row.id.startsWith(prefix + item.id + ':'));
        if (!source) return;
        const detail = source.lastElementChild;
        const options = Array.from(detail.querySelectorAll('dl > .product-option'));
        const sizeOption = options.find(option => /^sizes?:?$/i.test(option.querySelector('dt')?.textContent.trim() || ''));
        const otherOptions = options.filter(option => option !== sizeOption).map(option => option.textContent.trim());
        const title = detail.querySelector('.cart-notification-product__name')?.textContent.trim();
        const key = JSON.stringify([item.product_id || title, ...otherOptions]);
        let group = groups.get(key);
        if (!group) {
          const row = document.createElement('div');
          row.className = 'ld-notification-group';
          const image = source.querySelector('.cart-notification-product__image')?.cloneNode(true);
          if (image) row.appendChild(image);
          else row.classList.add('ld-notification-group--no-image');
          const content = detail.cloneNode(true);
          content.querySelectorAll('dl > .product-option').forEach(option => {
            if (/^sizes?:?$/i.test(option.querySelector('dt')?.textContent.trim() || '')) option.remove();
          });
          const sizes = document.createElement('div');
          sizes.className = 'ld-notification-sizes';
          content.appendChild(sizes);
          row.appendChild(content);
          fragment.appendChild(row);
          group = { sizes };
          groups.set(key, group);
        }
        const line = document.createElement('div');
        line.className = 'ld-notification-size';
        const size = document.createElement('span');
        size.textContent = sizeOption ? 'Size: ' + sizeOption.querySelector('dd').textContent.trim() : 'Quantity';
        const quantity = document.createElement('span');
        quantity.textContent = '× ' + item.quantity;
        line.append(size, quantity);
        group.sizes.appendChild(line);
      });
      productTarget.replaceChildren(fragment);
    }
    this.getSectionsToRender().forEach((section) => {
      if (bulkItems && section.id === 'cart-notification-product') return;
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
